/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("KeyboardService", ["InitService"], function (context, cls) {
  /**
   * @class KeyboardService
   * @memberOf classes
   * @gbcService
   */
  class KeyboardService /** @lends classes.KeyboardService.prototype */ {
    constructor() {
      /** @type {String} */
      this._bufferedKeys = null;
      /** @type {Boolean} */
      this._keyDownProcessed = false;
      /** @type {Object} */
      this._keymap = {
        8: "backspace",
        9: "tab",
        13: "enter",
        16: "shift",
        17: "ctrl",
        18: "alt",
        19: "pause",
        20: "capslock",
        27: "esc",
        32: "space",
        33: "pageup",
        34: "pagedown",
        35: "end",
        36: "home",
        37: "left",
        38: "up",
        39: "right",
        40: "down",
        45: "ins",
        46: "del",
        91: "meta",
        92: "meta",
        93: "contextmenu",
        112: "f1",
        113: "f2",
        114: "f3",
        115: "f4",
        116: "f5",
        117: "f6",
        118: "f7",
        119: "f8",
        120: "f9",
        121: "f10",
        122: "f11",
        123: "f12",
        144: "numlock",
        145: "scrolllock",
        224: "meta"
      };
      /** @type {Array} */
      this._navigationKeys = ["up", "down", "left", "right"];
      /** @type {Array<classes.WidgetBase>} */
      this._systemWidgetStack = [];
      /** when true, TAB / Shift+TAB are routed natively by the browser and DOM focus changes are mirrored to the VM via `focusin` -> Cursor ConfigureEvent. Every other key still flows through the regular VM pipeline. @type {Boolean} */
      this._accessibilityMode = false;
      /** While accessibility mode is on, the element currently carrying the `.gbc_Focus` visual class (sole owner; see `_onAccessibilityModeFocus`). @type {?Element} */
      this._accessibilityFocusedElement = null;
    }

    /**
     * @inheritDoc
     */
    init() {
      cls.DOMElementHelper.on(document.body, "keydown.KeyboardService", this._onKeyDown.bind(this));
      cls.DOMElementHelper.on(document.body, "keyup.KeyboardService", this._onKeyUp.bind(this));
      this._applyAccessibilityModePolicy();
    }

    /**
     * Resolve the accessibility mode and apply it to the service.
     * Priority order:
     *   1. URL query string overrides — `?accessibilityMode=1` forces ON,
     *      `?accessibilityMode=0` forces OFF. URL activation never touches storage.
     *   2. localStorage value persisted via the Settings checkbox (default: false).
     * @private
     */
    _applyAccessibilityModePolicy() {
      if (cls.WindowHelper.isURLParameterEnabled(globalThis.location.search, "accessibilityMode")) {
        this.setAccessibilityMode(true);
      } else if (cls.WindowHelper.isURLParameterDisabled(globalThis.location.search, "accessibilityMode")) {
        this.setAccessibilityMode(false);
      } else {
        this.setAccessibilityMode(context.StoredSettingsService.readAccessibilityMode());
      }
    }

    /**
     * Toggle the accessibility mode. When enabled: TAB / Shift+TAB are handled natively by the
     * browser (focus reaches every focusable element, not just the VM dialog graph), every other
     * key keeps flowing through the VM, a `focusin` listener mirrors focus changes back to the VM,
     * and the focus indicator follows the real DOM focus (see `_onAccessibilityModeFocus`).
     * Toggles the `gbc_AccessibilityMode` marker class on `<body>`.
     * @param {Boolean} enabled
     */
    setAccessibilityMode(enabled) {
      this._accessibilityMode = Boolean(enabled);

      if (this._accessibilityMode) {
        document.body.classList.add("gbc_AccessibilityMode");
        // `off` before `on` keeps the install idempotent (DOMElementHelper.on does not dedup).
        cls.DOMElementHelper.off(document.body, "focusin.KeyboardService");
        cls.DOMElementHelper.on(document.body, "focusin.KeyboardService", this._onAccessibilityModeFocus.bind(this));
      } else {
        document.body.classList.remove("gbc_AccessibilityMode");
        cls.DOMElementHelper.off(document.body, "focusin.KeyboardService");
        // Drop the DOM-driven focus class we owned; setFocusedWidget resumes managing it.
        if (this._accessibilityFocusedElement) {
          this._accessibilityFocusedElement.classList.remove(gbc.constants.widgetCssClasses.isFocused);
          this._accessibilityFocusedElement = null;
        }
      }

      // A live toggle must re-evaluate the focus trap of the modal currently on top: the trap is
      // otherwise only decided at show() time, so turning the mode on while a modal is already open
      // would leave the background reachable by Tab (and turning it off would leave it inert).
      const activeSystemWidget = this.getActiveSystemWidget();
      if (activeSystemWidget instanceof cls.ModalWidget) {
        activeSystemWidget.refreshModalFocusTrap();
      }
    }

    /**
     * @returns {boolean} whether the accessibility mode is currently active
     */
    isAccessibilityModeEnabled() {
      return this._accessibilityMode;
    }

    /**
     * True when GBC should hand the current TAB / Shift+TAB to the browser instead of the VM:
     * accessibility mode is on AND no system widget is active (system widgets keep first dibs).
     * @param {String} gbcKey
     * @returns {boolean}
     * @private
     */
    _shouldLetBrowserHandleTab(gbcKey) {
      const isTabKey = gbcKey === "tab" || gbcKey === "shift+tab";
      return isTabKey && this._accessibilityMode && !this.hasSystemWidget();
    }

    /**
     * `focusin` handler installed while accessibility mode is on. Two jobs:
     *  1. Visual — sole owner of `.gbc_Focus` (setFocusedWidget stops managing it): move it onto
     *     the DOM-focused widget's root so the ring follows real DOM focus, for ALL focusable
     *     widgets (including non-VM-focusable ones the VM never tracks).
     *  2. VM sync — for VM-focusable widgets, mirror the focus change to the VM via
     *     `_onRequestFocus` (-> focusVMCommand) so BEFORE FIELD triggers still fire.
     * No-op while a system widget is active (it owns its own focus/VM signalling).
     * @private
     */
    _onAccessibilityModeFocus(event) {
      if (this.hasSystemWidget()) {
        return;
      }

      // A dropdown/menu is not Tab-navigable: arrow keys move within it, Tab
      // leaves and closes it (APG menu pattern). When Tab moves DOM focus out
      // of an open dropdown, close every displayed dropdown that does not
      // contain the new target (focus moving *into* one keeps it open).
      cls.DropDownWidget.hideAll(event.target);

      const widget = context.WidgetService.getWidgetFromElement(event.target);
      if (!widget) {
        return;
      }

      // 1. Visual: the focus indicator follows the real DOM focus.
      this._updateAccessibilityFocusClass(widget);

      // 2. VM sync: only VM-focusable widgets are tracked. `hasFocus()` dedups and guards focus
      // moves between two sub-elements of one widget (e.g. DateEdit input <-> its picker button).
      if (widget.isVMFocusable() && !widget.hasFocus()) {
        widget._onRequestFocus(event);
      }
    }

    /**
     * Move `.gbc_Focus` onto `widget`'s root (where setFocusedWidget puts it too), clearing the
     * previously marked element. Sole owner of the focus visual while accessibility mode is on.
     * Styled widgets render their own `.gbc_Focus`; style-less nav widgets fall back to the
     * `:focus-visible` outline rule in widgetbase.scss.
     * @param {classes.WidgetBase} widget
     * @private
     */
    _updateAccessibilityFocusClass(widget) {
      const element = widget.getElement() || null;
      if (this._accessibilityFocusedElement === element) {
        return;
      }
      const focusClass = gbc.constants.widgetCssClasses.isFocused;
      if (this._accessibilityFocusedElement) {
        this._accessibilityFocusedElement.classList.remove(focusClass);
      }
      this._accessibilityFocusedElement = element;
      if (element) {
        element.classList.add(focusClass);
      }
    }

    /**
     * For composed keys (shift+key) and (ctrl+alt+key) where key.length = 1
     * transform keyString into the keyEvent.key char
     * Ex (shift+a --> 'A' or 'ctrl+alt+e' --> €)
     * @param {String} keyString - key string ex "shift+a"
     * @param {Object} keyEvent - dom key event
     * @returns {String} char for composed keys or unchanged keyString
     */
    getCharIfComposedKey(keyString, keyEvent) {
      if (keyEvent.key.length === 1 && (keyEvent.shiftKey || (keyEvent.ctrlKey && keyEvent.altKey))) {
        return keyEvent.key;
      }
      return keyString;
    }

    /**
     * Used the DBFormat definition to replace the keypad '.' by the good one
     * @param event
     * @returns {boolean} true if the translation is done
     * @private
     */
    _translateKeyAccordingDBFormat(event) {
      const currentApp = context.SessionService.getCurrent()?.getCurrentApplication();

      if (!currentApp) {
        return false;
      }

      const userInterfaceNode = currentApp.getNode(0);

      if (!userInterfaceNode.isAttributeSetByVM("decimalSeparator")) {
        return false;
      }

      const decimalSeparator = userInterfaceNode.attribute("decimalSeparator");

      if (event.key === "." && decimalSeparator !== event.key && event.code === "NumpadDecimal") {
        event.stopPropagation();
        cls.EventHelper.preventCancelableDefault(event);

        const newEvent = new cls.DelayedKeyCommand(currentApp, decimalSeparator, event);
        this._keyDownProcessed = this.processKey(decimalSeparator, event, false);

        if (!this._keyDownProcessed) {
          const focusedNode = currentApp?.getFocusedVMNodeAndValue(true);
          const widget = focusedNode && focusedNode.getWidget();

          if (widget) {
            newEvent.simulateBrowserBehaviorOnWidget(widget);
          }
        }

        this.executeKeyUp(decimalSeparator, event);

        return true;
      }

      return false;
    }

    /**
     * Keydown handler bound on UserInterface widget. Catch all keydown events and propagate it to the corresponding widget.
     * We detect the key combination and execute all actions bound to it. Otherwise, we let widget manage the key
     * @param event
     * @private
     */
    _onKeyDown(event) {
      event.gbcKey = this.getNormalizedKey(event);

      // hand TAB / Shift+TAB to the browser before any widget handler can preventDefault it;
      // the focusin listener mirrors the focus change back to the VM via a Cursor ConfigureEvent.
      if (this._shouldLetBrowserHandleTab(event.gbcKey)) {
        return;
      }

      // SystemWidget intercept: if a GBC-managed system widget is active, route all keys to it.
      if (this.hasSystemWidget()) {
        const activeSystemWidget = this.getActiveSystemWidget();
        let handled = false;

        // First, let the focused child widget handle the key (e.g. Space on checkbox, arrows on combobox).
        // Guard against focus having escaped outside the active system widget (no focus trap is installed).
        const focusedWidget = context.WidgetService.getWidgetFromElement(document.activeElement);
        if (focusedWidget && focusedWidget !== activeSystemWidget && activeSystemWidget.getElement().contains(focusedWidget.getElement())) {
          handled = focusedWidget.managePriorityKeyDown(event.gbcKey, event, false) || focusedWidget.manageKeyDown(event.gbcKey, event, false);
        }

        // If the focused widget didn't handle it, let the system widget handle it (e.g. ESC to close modal)
        if (!handled) {
          handled =
            activeSystemWidget.managePriorityKeyDown(event.gbcKey, event, false) || activeSystemWidget.manageKeyDown(event.gbcKey, event, false);
        }

        context.LogService.keyboard.log("onKeyDown: systemWidget key:", event.gbcKey);
        event.stopPropagation();
        if (handled) {
          cls.EventHelper.preventCancelableDefault(event);
        }

        return;
      }

      if (!this._canProcessEvent()) {
        return;
      }

      if (this._translateKeyAccordingDBFormat(event)) {
        return;
      }

      const contentEditable = Boolean(event.target.getAttribute("contenteditable")) && event.target.getAttribute("contenteditable") !== "false";
      const isInputable =
        event.target.tagName === "TEXTAREA" || event.target.tagName === "INPUT" || (event.target.tagName === "DIV" && contentEditable);

      if (event.ctrlKey) {
        if (event.which === 80 /* p */ || event.which === 83 /* s */) {
          cls.EventHelper.preventCancelableDefault(event);
        }
        if (event.which === 65 /* a */ && !isInputable) {
          cls.EventHelper.preventCancelableDefault(event);
        }
      }

      if (event.which === 8 /* backspace */ && (!isInputable || event.target.readOnly)) {
        cls.EventHelper.preventCancelableDefault(event); // inhibit previous page on backspace
      }

      let combi = this.translateKeys(event, event.gbcKey);

      if (cls.WindowHelper.isAndroidEmulationDebug()) {
        // Try to emulate android behavior

        if (combi === "tab") {
          // try to emulate "next field" virtual keyboard key
          cls.EventHelper.preventCancelableDefault(event);
          event.stopPropagation();
          this._emulateAndroidNextFieldVirtualKey();
        } // keyDown keyCode are not raised correctly on android, so set key as "unidentified"
        combi = "unidentified";
      }

      // if user keep pressing same key without releasing it and typeahead is flagged as frozen, we don't push event
      this._keyDownProcessed = false;

      const repeatKey = this._bufferedKeys === combi;

      if (combi && !combi.includes("unidentified")) {
        this._bufferedKeys = combi;
        context.LogService.keyboard.log("KEY USED onKeyDown saved as bufferedKeys : ", this._bufferedKeys, event.key);

        cls.KeyboardHelper.updateCapslockFlag(event);

        const currentApp = context.SessionService.getCurrent()?.getCurrentApplication();

        if (!currentApp) {
          return;
        }

        const focusedNode = currentApp.getFocusedVMNodeAndValue(true);

        // If DVM is not idle (aka processing), take care of interrupt accelerators
        if (currentApp.isProcessing()) {
          const vmKey = cls.KeyboardHelper.convertBrowserKeyToVMKey(this._bufferedKeys);
          const actionNode = currentApp.action.getDefaultActionForKey(vmKey, currentApp.uiNode());
          const actionName = actionNode?.attribute("name");

          if (actionName === "interrupt") {
            context.LogService.keyboard.log("onKeyDown: sending interrupt to the VM because vmKey=", vmKey);
            event.stopPropagation();
            cls.EventHelper.preventCancelableDefault(event);
            this._keyDownProcessed = true;
            currentApp.interrupt();
            return;
          }
        }

        // Add delayed keys when (vm is processing -aka not idle- or when there are already pending commands) and no webcomponent is inside the current focused node.
        // They may be background process such as current focused node being destroyed when checking on webcomponent
        if (
          (currentApp.isProcessing() || !currentApp.scheduler.hasNothingToProcess()) &&
          (!focusedNode || focusedNode.getFirstChild("WebComponent") === null)
        ) {
          event.stopPropagation();
          cls.EventHelper.preventCancelableDefault(event);

          if (!repeatKey) {
            // don't delay repeated key, simply ignore it
            currentApp.scheduler.delayedKeyCommand(this._bufferedKeys, event);
          }
        } else {
          this._keyDownProcessed = this.processKey(this._bufferedKeys, event, repeatKey);
        }
      }
    }

    /**
     * Try to emulate "Next field" virtual keyboard behavior
     * @private
     */
    _emulateAndroidNextFieldVirtualKey() {
      const focusedNode = context.SessionService.getCurrent()?.getCurrentApplication()?.getFocusedVMNodeAndValue(true);
      const focusedWidget = focusedNode.getWidget();

      let found = false;
      if (focusedWidget.getInputElement()) {
        // go through all element of the FORM
        for (const field of focusedWidget.getInputElement().form) {
          // if field is after the current one, and is not readonly focus it
          if (found && !field.hasAttribute("readonly")) {
            field.focus();
            break;
          }
          // search current field
          if (field === focusedWidget.getInputElement()) {
            found = true;
          }
        }
      }
    }

    /**
     * Keyup handler bound on UserInterface widget. Catch all keyup events and propagate it to the corresponding widget.
     * Key combination previously detected in Keydown is passed as parameter
     * @param event
     * @private
     */
    _onKeyUp(event) {
      event.gbcKey = this.getNormalizedKey(event);
      // mirror _onKeyDown so widgets do not see a keyup without a matching
      // keydown — would otherwise confuse press/release tracking.
      if (this._shouldLetBrowserHandleTab(event.gbcKey)) {
        return;
      }
      if (this.hasSystemWidget()) {
        event.stopPropagation();
        this._bufferedKeys = null;
        return;
      }

      if (!this._canProcessEvent()) {
        return;
      }

      if (!event.gbcKey.toLowerCase().includes("unidentified")) {
        // if scheduler isn't finished, don't manage key up it will be done by DelayedKey command
        if (!context.SessionService.getCurrent()?.getCurrentApplication()?.scheduler.hasNoCommandToProcess()) {
          event.stopPropagation();
          cls.EventHelper.preventCancelableDefault(event);
        } else {
          this.executeKeyUp(this._bufferedKeys, event);
        }
      }
      this._bufferedKeys = null;
    }

    /**
     * @param {String} keyString string corresponding to the key
     * @param {Object} keyEvent keyDown js event
     */
    executeKeyUp(keyString, keyEvent) {
      const focusedNode = context.SessionService.getCurrent()?.getCurrentApplication()?.getFocusedVMNodeAndValue(true);
      // we pass bufferedKeys as parameter in order to be able to be sure to manage previously typed combination.
      focusedNode?.getWidget()?.manageKeyUp(keyString, keyEvent);
    }

    /**
     * Register a widget as an active system widget.
     * @param {classes.WidgetBase} widget
     */
    registerSystemWidget(widget) {
      if (!this._systemWidgetStack.includes(widget)) {
        this._systemWidgetStack.push(widget);
      }
    }

    /**
     * Unregister a widget from the system widget stack.
     * @param {classes.WidgetBase} widget
     */
    unregisterSystemWidget(widget) {
      if (!widget) {
        return;
      }
      const idx = this._systemWidgetStack.lastIndexOf(widget);
      if (idx !== -1) {
        this._systemWidgetStack.splice(idx, 1);
      }
    }

    /**
     * Returns true if any active system widget is on the stack.
     * @returns {boolean}
     */
    hasSystemWidget() {
      return this._systemWidgetStack.length > 0;
    }

    /**
     * Returns the topmost active system widget.
     * @returns {classes.WidgetBase|null}
     */
    getActiveSystemWidget() {
      return this._systemWidgetStack.length > 0 ? this._systemWidgetStack.at(-1) : null;
    }

    /**
     * Check if key is a basic modifier (ctrl, shift, alt)
     * @param {string} key
     * @returns {boolean} returns true if key is a basic modifier
     */
    isBasicModifier(key) {
      const k = key.toLowerCase();
      return k === "shift" || k === "ctrl" || k === "alt" || k === "meta" || k === "dead";
    }

    /**
     * Returns key string corresponding to key code
     * @param {Number} key
     * @return {String}
     */
    getKeyMap(key) {
      return this._keymap[key];
    }

    /**
     * Normalize DOM event key using VM modifier common names
     * @param event
     * @returns {*|string} returns normalized key
     */
    getNormalizedKey(event) {
      let key = this._keymap[event.which] || "";

      // Difference between Numpad keys and "normal" keys
      if (key === "enter" && event.code !== "NumpadEnter") {
        key = "return";
      } else if (key === "del" && event.code !== "NumpadDecimal") {
        key = "delete";
      } else if (key === "ins" && event.code !== "Numpad0") {
        key = "insert";
      }

      if (!key) {
        key = event.key;
      }

      if (!key) {
        key = String.fromCodePoint(event.which || event.code);
      }

      return key;
    }

    /**
     * Get the combination of keys (modifiers included) being typed
     * @param event
     * @param {string} normalizedKey
     * @returns {string} returns normalized keys combination
     */
    translateKeys(event, normalizedKey) {
      const ctrlKey = event.ctrlKey;
      const altKey = event.altKey;
      const shiftKey = event.shiftKey;
      const metaKey = event.metaKey;

      let keys = "";
      if (!this.isBasicModifier(normalizedKey)) {
        if (metaKey) {
          if (keys.length !== 0) {
            keys += "+";
          }
          keys += "meta";
        }
        // use that order : ctrl+shift+alt
        if (ctrlKey) {
          if (keys.length !== 0) {
            keys += "+";
          }
          keys += "ctrl";
        }
        if (altKey) {
          if (keys.length !== 0) {
            keys += "+";
          }
          keys += "alt";
        }
        if (shiftKey) {
          if (keys.length !== 0) {
            keys += "+";
          }
          keys += "shift";
        }
        if (keys.length !== 0) {
          keys += "+";
        }
        keys += normalizedKey;
      }
      return keys.length === 1 ? keys : keys.toLowerCase();
    }

    /**
     *
     * @param {String} keyString - string of the key (including combination, ctrl+, shift+, ...)
     * @param {KeyboardEvent} event - dom key event
     * @param {boolean} repeat - true if key is being pressed
     * @returns {boolean} true if the key has been processed, false otherwise
     */
    processKey(keyString, event, repeat) {
      context.LogService.keyboard.log("processKey: keyString:", keyString);

      let processed = false;

      const app = context.SessionService.getCurrent()?.getCurrentApplication();
      const focusedNode = app?.getFocusedVMNodeAndValue(true);
      const activeDropDownWidget = app?.focus.getActiveDropDownWidget();

      // Search widget
      let widget = focusedNode && focusedNode.getWidget();
      if (!widget && focusedNode?.getTag() === "Matrix") {
        // If focused node is a matrix look for scrollgrid widget
        // because there is no Matrix widget
        let scrollGridNode = focusedNode.getAncestor("ScrollGrid");
        if (scrollGridNode) {
          widget = scrollGridNode.getWidget();
        }
      }

      // TODO gbc-3832 : this special case, initially created for tables, is maybe not needed anymore
      // 1. Special case CTRL+C, if there is a text selection and the current VM focus has not DOM focus
      if (widget && (keyString === "ctrl+c" || keyString === "meta+c")) {
        context.LogService.keyboard.log("processKey: special case (ctrl+c on a widget)");

        // Case where the RTable is not active but the selection is made on it
        const tableWithItemsSelection = cls.RTableWidget.getTableWithItemsSelection();
        if (tableWithItemsSelection) {
          tableWithItemsSelection._copySelectionInClipboard();
          processed = true;
        } else {
          const selectionText = cls.WindowHelper.getSelectionText();
          const domFocus = widget.hasDOMFocus();
          if (!domFocus && selectionText && selectionText !== "") {
            // return false, to let the browser manage CTRL+C
            return false;
          }
        }
      }

      // In accessibility mode the browser drives DOM focus, which can land on a widget that is
      // NOT the VM-focused one (an action BUTTON outside the dialog graph, a toolbar item…). Give
      // that DOM-focused widget priority on key handling so it stays operable (Space/Enter ->
      // activate) through its own managePriorityKeyDown/manageKeyDown, instead of relying on
      // native element activation (div-based widgets have none, and it is browser-dependent).
      let domFocusedWidget = null;
      if (this._accessibilityMode) {
        const activeWidget = context.WidgetService.getWidgetFromElement(document.activeElement);
        if (activeWidget && activeWidget !== widget) {
          domFocusedWidget = activeWidget;
        }
      }

      // 2. Priority key managed by the widget before any action
      if (!processed) {
        // manage opened dropdowns, floating panel or sidebar in priority
        processed =
          activeDropDownWidget?.managePriorityKeyDown(keyString, event, repeat) ||
          context.MainContainerService.managePriorityKeyDown(keyString, event, repeat);
        context.LogService.keyboard.log("processKey: closed a dropdown, floating panel or sidebar");
      }
      if (!processed && domFocusedWidget?.isVisible()) {
        processed = domFocusedWidget.managePriorityKeyDown(keyString, event, repeat);
      }
      if (!processed && widget && widget.isVisible()) {
        context.LogService.keyboard.log("processKey: widget.managePriorityKeyDown");

        processed = widget.managePriorityKeyDown(keyString, event, repeat);
      }

      // 3. Try to process action for combination key
      if (!processed) {
        processed = this._processAction(keyString);
        context.LogService.keyboard.log("processKey: KeyBoardService._processAction: ", keyString);
      }
      // 4. If there is no accelerator defined on combination when shift key is pressed,
      // we try to process action on shifted key directly but only for key which can be displayed.
      // Example : we need this to manage ? accelerator
      if (!processed && event.shiftKey === true && event.key && event.key.length === 1) {
        processed = this._processAction(event.key);
        context.LogService.keyboard.log("processKey: KeyBoardService._processAction: ", event.key);
      }

      // 5. if key not processed, ask widget to manage it
      if (!processed && activeDropDownWidget) {
        // first widget with an active DropDown catches the keys
        context.LogService.keyboard.log("processKey: there is an open dropdown: dropDown.manageKeyDown");

        processed = activeDropDownWidget.manageKeyDown(keyString, event, repeat);
      }
      if (!processed && domFocusedWidget?.isVisible()) {
        processed = domFocusedWidget.manageKeyDown(keyString, event, repeat);
      }
      if (!processed && widget && widget.isVisible()) {
        processed = widget.manageKeyDown(keyString, event, repeat);
      }

      // 6. Check if key must be sent to VM (for specific nodes and specific keys we always send to VM)
      if (!processed) {
        let vmKey = cls.KeyboardHelper.convertBrowserKeyToVMKey(keyString);
        let createKeyEvent = false;

        if (widget?.isVisible()) {
          if (widget.isInstanceOf(cls.FieldWidgetBase)) {
            if (keyString === "tab" || keyString === "shift+tab") {
              createKeyEvent = true;
            }
          } else if (widget.isInstanceOf(cls.TableWidgetBase) || widget.isInArray()) {
            if (keyString === "up" || keyString === "down" || keyString === "tab" || keyString === "shift+tab") {
              createKeyEvent = true;
            } else {
              // transform "shift+a" vmKey into "A" to send the correct key at VM
              // TODO we should probably do this more globally
              vmKey = context.KeyboardService.getCharIfComposedKey(vmKey, event);

              // letter should be sent to VM for "search" feature
              createKeyEvent =
                focusedNode.attribute("dialogType") === "DisplayArray" &&
                vmKey.length === 1 &&
                (cls.KeyboardHelper.isLetter(vmKey) || cls.KeyboardHelper.isNumeric(vmKey));
            }
          }
        }
        if (focusedNode?.getTag() === "MenuAction") {
          // first letter of actions and nav keys should be sent to VM to navigate in the menu
          createKeyEvent = this._isMenuActionShortcutKey(focusedNode.getParentNode(), vmKey) || this._navigationKeys.indexOf(keyString) >= 0;
        }

        if (createKeyEvent) {
          context.LogService.keyboard.log("processKey send KeyEvent for key : ", vmKey);
          app.action.sendKey(vmKey, focusedNode);
          processed = true;
        }
      }

      // Handle keys for UR (like F11 to toggle full screen)
      if (!processed) {
        // nativeCall return might be undefined on old UR versions -> undefined means false here
        // Note that GBC browser implementation returns false, the next statement won't consider key as processed
        processed = !!context.__wrapper.nativeCall(
          context.__wrapper.param(
            {
              name: "keyEvent",
              args: {
                key: keyString
              }
            },
            app
          )
        );
      }

      // 7. Manage dom event, stopPropagation preventDefault if key has been processed
      if (event) {
        if (processed) {
          if (!event.allowPropagation) {
            event.stopPropagation();
          }
          if (event.allowDefault) {
            // Processed was true as a trick to skip the previous steps, now we can revert it to let the browser deal with the event
            processed = false;
          } else {
            cls.EventHelper.preventCancelableDefault(event);
          }
        } else {
          // check if existing modal menu
          const uiNode = app.uiNode();
          const currentWindow = uiNode && app.getNode(uiNode.attribute("currentWindow"));
          const modalMenu = currentWindow && currentWindow.getFirstChildWithAttribute("Menu", "active", 1);
          if (modalMenu) {
            const modalType = modalMenu.attribute("style");
            if (modalType === "winmsg" || modalType === "dialog") {
              // dialog detected, we need to prevent default to avoid focus being moved to another application
              event.stopPropagation();
              cls.EventHelper.preventCancelableDefault(event);
            }
          }
        }
      }
      return processed;
    }

    /**
     * Check if key is a menu action shortcut
     * @param {classes.NodeBase} menuNode
     * @param {string} key
     * @returns {boolean} returns true if key is a shortcut of the passed menu node
     * @private
     */
    _isMenuActionShortcutKey(menuNode, key) {
      const menuActions = menuNode.getChildren("MenuAction");
      for (const element of menuActions) {
        const menuAction = element;
        if (menuAction.attribute("active")) {
          let text = menuAction.isAttributeSetByVM("text") ? menuAction.attribute("text") : menuAction.attribute("name");
          text = text.toString().toLocaleLowerCase();
          if (text && text.length !== 0 && text[0] === key) {
            return true;
          }
        }
      }
      return false;
    }

    /**
     * Find and execute VM action corresponding to key
     * @param {string} keyString - string of the key (including combination, ctrl+, shift+, ...)
     * @returns {boolean} true if the key has been processed, false otherwise
     */
    _processAction(keyString) {
      const vmKey = cls.KeyboardHelper.convertBrowserKeyToVMKey(keyString);
      const actionService = context.SessionService.getCurrent()?.getCurrentApplication()?.action;

      // Try to find corresponding action in active dialog actions list
      const actionNode = actionService.getActiveDialogActionForKey(vmKey);
      if (actionNode) {
        const cmd = actionService.executeAction(actionNode, {
          sendValue: true
        });
        return cmd !== null;
      }

      // if no action in active dialog, try to process a local action
      return this._processLocalAction(keyString);
    }

    /**
     * Find and execute VM local action corresponding to key
     * @param {string} keyString - string of the key (including combination, ctrl+, shift+, ...)
     * @returns {boolean} true if the key has been processed, false otherwise
     */
    _processLocalAction(keyString) {
      let actionNode = null;
      let actionName = null;

      const vmKey = cls.KeyboardHelper.convertBrowserKeyToVMKey(keyString);
      const app = context.SessionService.getCurrent()?.getCurrentApplication();
      const focusedNode = app?.getFocusedVMNodeAndValue(true);
      const actionService = app?.action;

      const activeForm = focusedNode && focusedNode.getAncestor("Form");

      // 1. Try to find corresponding action in current form action defaults list
      if (activeForm) {
        actionNode = actionService.getDefaultActionForKey(vmKey, activeForm);
        actionName = actionNode?.attribute("name");
      }

      // 2. Try to find corresponding action in application global action defaults list
      if (!actionNode) {
        context.LogService.keyboard.log("_processLocalAction: actionService.getDefaultActionForKey in application: vmKey:", vmKey);

        actionNode = actionService.getDefaultActionForKey(vmKey, app.uiNode());
        if (actionNode) {
          actionName = actionNode.attribute("name");

          context.LogService.keyboard.log("_processLocalAction: actionName :", actionName);

          if (activeForm && actionService.getDefaultActionForName(actionName, activeForm)) {
            return true; // return true to be sure that key will not be processed after
          }
        }
      }

      // 3. Try to find corresponding action from localAction GBC list
      if (!actionNode) {
        actionName = cls.ActionApplicationService.getLocalActionName(vmKey);
      }

      context.LogService.keyboard.log("_processLocalAction: actionService.executeLocalAction: actionName:", actionName);
      return actionService.executeLocalAction(actionName);
    }

    /**
     * @return {boolean} false if the application is waiting in background
     * @private
     */
    _canProcessEvent() {
      const curSession = context.SessionService.getCurrent();
      const application = curSession && curSession.getCurrentApplication();

      if (application) {
        return application.canProcessEvent();
      }

      return true;
    }
  }

  gbc.KeyboardService = new KeyboardService();
  context.InitService.register(context.KeyboardService);
});
